/**
 * FmMapTimeline — Temporal playback for map data: growth, hazard, weather, operations.
 *
 * Props are SEMANTIC ONLY:
 *  - start: earliest available date/year
 *  - end: latest available date/year
 *  - current: currently displayed date/year
 *  - steps: array of timesteps ([2024, 2025, 2026, ...] or ['2024-01', '2024-02', ...])
 *  - mode: 'historical' | 'forecast' | 'scenario'
 *  - onStepChange: (newStep) => void
 *
 * REQUIREMENT: pages pass semantic time ranges and step lists only.
 * DS owns the scrubber UI, playback, animation timing, and accessibility.
 */

import React, { useCallback, useMemo } from 'react';
import './FmMapTimeline.css';

export interface FmMapTimelineProps {
  /** Earliest available step/date */
  start?: string | number;
  /** Latest available step/date */
  end?: string | number;
  /** Currently displayed step/date */
  current?: string | number;
  /** Array of available steps (years, dates, or step IDs) */
  steps?: Array<string | number>;
  /** Temporal mode: 'historical' (observed) | 'forecast' (model) | 'scenario' (plan) */
  mode?: 'historical' | 'forecast' | 'scenario';
  /**
   * Localized mode-badge label (consumer i18n). REQUIRED so the badge is never a
   * bare English/raw-enum "FORECAST" on a non-English surface — there is no
   * fallback to the raw `mode` value.
   */
  modeLabel: string;
  /** Localized accessible name for the scrubber (consumer i18n). REQUIRED (no English fallback). */
  scrubberLabel: string;
  /**
   * Per-step text risk qualifier, index-aligned to `steps` (consumer i18n),
   * e.g. a severity/hazard note shown under each step. Only pass when a genuine
   * per-step signal exists.
   */
  qualifiers?: Array<string | undefined>;
  /** Called when user selects a new step */
  onStepChange?: (step: string | number) => void;
  /**
   * Auto-play controls. The play/pause a11y labels are REQUIRED to render them —
   * autoplay only shows when `onAutoPlayToggle` AND both localized labels are
   * present, so no hardcoded English label can ever reach the DOM.
   */
  autoPlay?: boolean;
  onAutoPlayToggle?: (enabled: boolean) => void;
  /** Localized "Play" a11y label (consumer i18n). */
  playLabel?: string;
  /** Localized "Pause" a11y label (consumer i18n). */
  pauseLabel?: string;
  /** CSS class */
  className?: string;
  'data-testid'?: string;
}

/**
 * FmMapTimeline — scrubber and step selector for temporal map data.
 * All semantic: pages pass step arrays and mode names, not raw animation logic.
 */
export const FmMapTimeline: React.FC<FmMapTimelineProps> = ({
  start,
  end,
  current,
  steps = [],
  mode = 'historical',
  modeLabel,
  scrubberLabel,
  qualifiers,
  onStepChange,
  autoPlay = false,
  onAutoPlayToggle,
  playLabel,
  pauseLabel,
  className,
  'data-testid': testId,
}) => {
  const classes = ['fm-map-timeline', `timeline-mode-${mode}`, className]
    .filter(Boolean)
    .join(' ');

  // Find index of current step
  const currentIdx = useMemo(() => {
    if (!current || steps.length === 0) return 0;
    return steps.findIndex((s) => s === current);
  }, [current, steps]);

  const handleStepClick = useCallback(
    (step: string | number) => {
      onStepChange?.(step);
    },
    [onStepChange],
  );

  const handleScrubberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const idx = parseInt(e.target.value, 10);
      if (idx >= 0 && idx < steps.length) {
        onStepChange?.(steps[idx]);
      }
    },
    [steps, onStepChange],
  );

  if (steps.length === 0) {
    return null;
  }

  return (
    <div className={classes} data-testid={testId}>
      {/* Mode badge */}
      <div className="timeline-header">
        <span className={`mode-badge mode-badge--${mode}`}>{modeLabel}</span>
        {start && end && (
          <span className="timeline-range">
            {start}–{end}
          </span>
        )}
      </div>

      {/* Scrubber */}
      <div className="timeline-scrubber-row">
        <input
          type="range"
          min="0"
          max={steps.length - 1}
          value={currentIdx}
          onChange={handleScrubberChange}
          className="timeline-scrubber"
          aria-label={scrubberLabel}
        />
      </div>

      {/* Step buttons — each carries an optional per-step risk qualifier. */}
      <div className="timeline-steps">
        {steps.map((step, idx) => {
          const qualifier = qualifiers?.[idx];
          return (
            <button
              key={`timeline-step-${String(step)}`}
              className={`timeline-step ${currentIdx === idx ? 'active' : ''}`}
              onClick={() => handleStepClick(step)}
              aria-pressed={currentIdx === idx}
              title={qualifier ? `${step} · ${qualifier}` : String(step)}
            >
              <span className="timeline-step__label">{step}</span>
              {qualifier ? <span className="timeline-step__qualifier">{qualifier}</span> : null}
            </button>
          );
        })}
      </div>

      {/* Auto-play toggle — rendered ONLY when both localized a11y labels are
       * supplied, so no hardcoded English "Play"/"Pause" can reach the DOM. */}
      {onAutoPlayToggle && playLabel && pauseLabel && (
        <div className="timeline-controls">
          <button
            className={`autoplay-button ${autoPlay ? 'playing' : ''}`}
            onClick={() => onAutoPlayToggle(!autoPlay)}
            aria-label={autoPlay ? pauseLabel : playLabel}
          >
            <span aria-hidden="true">{autoPlay ? '⏸' : '▶'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default FmMapTimeline;
